import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import VideoPlayerModal from './VideoPlayerModal';

const LEVELS = [
  { min: 4, max: 7, label: 'AI Newcomer' },
  { min: 8, max: 11, label: 'Early Explorer' },
  { min: 12, max: 15, label: 'Developing Practitioner' },
  { min: 16, max: 18, label: 'Active Practitioner' },
  { min: 19, max: 20, label: 'AI Champion' },
];

const DIM_NOTES = {
  freq: ['Never using AI tools', 'Starting to experiment', 'Using sometimes', 'Using regularly', 'Daily AI user'],
  conf: ['Significant confidence gap', 'Early confidence building', 'Growing confidence', 'Mostly confident', 'Fully confident'],
  range: ['No use cases yet', 'Narrow use case set', 'Moderate range', 'Broad application', 'Wide strategic use'],
  adv: ['AI skeptic or worried', 'Cautiously uncertain', 'Curious and open', 'Positive advocate', 'Enthusiastic champion'],
};

export default function Dashboard({ user, onNav }) {
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [welcomeVideo, setWelcomeVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [assessSnap, profileSnap, welcomeSnap] = await Promise.all([
        getDoc(doc(db, 'assessments', user.uid)),
        getDoc(doc(db, 'users', user.uid)),
        getDoc(doc(db, 'videos', 'welcome')),
      ]);
      if (assessSnap.exists()) setData(assessSnap.data());
      if (profileSnap.exists()) setProfile(profileSnap.data());
      if (welcomeSnap.exists()) {
        const v = welcomeSnap.data();
        if (v.active && v.embedUrl) setWelcomeVideo(v);
      }
      setLoading(false);
    };
    load();
  }, [user.uid]);

  if (loading) return <div style={{ color: '#8A8A8A', fontSize: '14px' }}>Loading your dashboard...</div>;

  const pre = data?.pre;
  const post = data?.post;
  const current = post || pre;
  const level = current ? (LEVELS.find(l => current.scores.total >= l.min && current.scores.total <= l.max) || LEVELS[0]) : null;

  // First name resolution. Prefer Firestore profile.firstName, fall back to auth displayName first token,
  // fall back to null which triggers the generic headline.
  const firstName = profile?.firstName || (user.displayName ? user.displayName.split(' ')[0] : null);

  const dims = [
    { key: 'freq', label: 'Frequency' },
    { key: 'conf', label: 'Confidence' },
    { key: 'range', label: 'Use Case Range' },
    { key: 'adv', label: 'Advocacy' },
  ];

  const WelcomeVideoCard = () => {
    if (!welcomeVideo) return null;
    return (
      <div style={styles.videoCard} onClick={() => setShowVideoModal(true)}>
        <div style={styles.videoIconBox}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 5v14l11-7z" stroke="#D4A847" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
          </svg>
        </div>
        <div style={styles.videoTextBlock}>
          <div style={styles.videoTitle}>
            {firstName ? `Hey there, ${firstName}` : 'Welcome to From Curious to Capable'}
          </div>
          <div style={styles.videoSub}>Watch your welcome video, 5 minutes</div>
        </div>
        <button
          style={styles.videoBtn}
          onClick={(e) => { e.stopPropagation(); setShowVideoModal(true); }}
        >
          Watch now
        </button>
      </div>
    );
  };

  if (!pre) {
    return (
      <div>
        <div className="page-heading">Welcome{firstName ? `, ${firstName}` : ''}.</div>
        <div className="page-sub">Let us get your baseline before the program begins.</div>

        <WelcomeVideoCard />

        <div style={styles.welcomeCard}>
          <div style={styles.welcomeTitle}>Start with your AI Readiness Snapshot</div>
          <div style={styles.welcomeText}>
            Before anything else, we need a real picture of where you are today. No right answers.
            No test. Just eight honest questions that help us build a training experience that actually meets you where you are.
          </div>
          <div style={styles.metaRow}>
            <div style={styles.metaItem}><div style={styles.metaDot}></div>Takes about 8 minutes</div>
            <div style={styles.metaItem}><div style={styles.metaDot}></div>4 dimensions measured</div>
            <div style={styles.metaItem}><div style={styles.metaDot}></div>Results shown immediately</div>
          </div>
          <button className="btn-primary" onClick={() => onNav('assessment')}>Take the Snapshot →</button>
        </div>

        <VideoPlayerModal
          isOpen={showVideoModal}
          onClose={() => setShowVideoModal(false)}
          embedUrl={welcomeVideo?.embedUrl || ''}
          title="Welcome Video"
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-heading">
        {firstName ? `${firstName}'s Dashboard` : 'Your Dashboard'}
      </div>
      <div className="page-sub">
        {post ? 'Post-training results, see how far you have come.' : `Baseline recorded${pre?.completedAt?.toDate ? ' on ' + pre.completedAt.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}.`}
      </div>

      <WelcomeVideoCard />

      <div style={styles.scoreHero}>
        <div style={styles.scoreLeft}>
          <div style={styles.scoreLabel}>{post ? 'Final Score' : 'Baseline Score'}</div>
          <div style={styles.scoreBig}>{current.scores.total}<span style={styles.scoreOf}>/20</span></div>
        </div>
        <div style={styles.scoreRight}>
          <div style={styles.scoreBadge}>{post ? 'Post-Training' : 'Pre-Training Baseline'}</div>
          <div style={styles.scoreLevel}>{level.label}</div>
          <div style={styles.scoreTagline}>
            {post && pre && `You moved from ${pre.scores.total} to ${post.scores.total}, a gain of +${post.scores.total - pre.scores.total} points across all four dimensions.`}
            {!post && 'Your scores will update after you complete the post-training assessment.'}
          </div>
        </div>
      </div>

      <div className="dim-grid">
        {dims.map(d => (
          <div className="dim-card" key={d.key}>
            <div className="dim-name">{d.label}</div>
            <div className="dim-score">{current.scores[d.key]}<span> /5</span></div>
            <div className="dim-bar"><div className="dim-fill" style={{ width: `${((current.scores[d.key]-1)/4)*100}%` }} /></div>
            <div className="dim-label">{DIM_NOTES[d.key][(current.scores[d.key] || 1) - 1]}</div>
          </div>
        ))}
      </div>

      {pre && post && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={styles.panelTitle}>Before vs. After Comparison</div>
          <div style={styles.compareHead}>
            <div style={styles.compareLabel}>Dimension</div>
            <div style={styles.compareLabel}>Progress</div>
            <div style={styles.compareLabel}>Before</div>
            <div style={styles.compareLabel}>After</div>
            <div style={styles.compareLabel}>Change</div>
          </div>
          {dims.map(d => {
            const before = pre.scores[d.key];
            const after = post.scores[d.key];
            const delta = after - before;
            return (
              <div key={d.key} style={styles.compareRow}>
                <div style={styles.dimName}>{d.label}</div>
                <div style={{ position: 'relative', height: '6px', background: '#E8E6E2', flex: 1 }}>
                  <div style={{ position: 'absolute', height: '100%', background: '#C0C0C0', width: `${((before-1)/4)*100}%` }} />
                  <div style={{ position: 'absolute', height: '100%', background: 'linear-gradient(90deg,#C0392B,#D4A847)', width: `${((after-1)/4)*100}%` }} />
                </div>
                <div style={styles.compareScore}>{before}</div>
                <div style={styles.compareScore}><strong>{after}</strong></div>
                <div style={{ ...styles.compareScore, color: delta > 0 ? '#1a8a4a' : '#8A8A8A', fontWeight: '600' }}>
                  {delta > 0 ? `+${delta}` : delta === 0 ? '—' : delta}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!post && (
        <div style={styles.ctaBanner}>
          <div>
            <div style={styles.ctaTitle}>Post-Training Assessment</div>
            <div style={styles.ctaSub}>Complete this after your training to see how far you have come.</div>
          </div>
          <button style={styles.ctaBtn} onClick={() => onNav('assessment')}>Take It Now →</button>
        </div>
      )}

      <VideoPlayerModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        embedUrl={welcomeVideo?.embedUrl || ''}
        title="Welcome Video"
      />
    </div>
  );
}

const styles = {
  welcomeCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '36px', maxWidth: '560px' },
  welcomeTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  welcomeText: { fontSize: '14px', color: '#3D3D3D', fontWeight: '300', lineHeight: '1.7', marginBottom: '20px' },
  metaRow: { display: 'flex', gap: '20px', marginBottom: '24px' },
  metaItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#8A8A8A' },
  metaDot: { width: '5px', height: '5px', borderRadius: '50%', background: '#D4A847' },

  videoCard: {
    background: '#FFFFFF',
    border: '2px solid #D4A847',
    padding: '32px 40px',
    display: 'flex',
    alignItems: 'center',
    gap: '28px',
    marginBottom: '32px',
    cursor: 'pointer',
    transition: 'border-color 0.15s, transform 0.15s',
    boxShadow: '0 4px 16px rgba(27, 42, 74, 0.08)',
  },
  videoIconBox: {
    width: '88px',
    height: '88px',
    background: '#1B2A4A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  videoTextBlock: { flex: 1, minWidth: 0 },
  videoTitle: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '26px',
    fontWeight: '700',
    color: '#1B2A4A',
    marginBottom: '8px',
    lineHeight: '1.2',
  },
  videoSub: { fontSize: '15px', color: '#3D3D3D', fontWeight: '400' },
  videoBtn: {
    background: '#1B2A4A',
    color: '#FFFFFF',
    border: 'none',
    padding: '16px 32px',
    fontFamily: 'Playfair Display, serif',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  scoreHero: { background: '#1B2A4A', padding: '28px 32px', display: 'flex', alignItems: 'center', gap: '32px', marginBottom: '24px' },
  scoreLeft: { textAlign: 'center', paddingRight: '28px', borderRight: '1px solid rgba(255,255,255,0.1)' },
  scoreLabel: { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '8px', fontWeight: '600' },
  scoreBig: { fontFamily: 'Playfair Display, serif', fontSize: '64px', fontWeight: '900', color: '#fff', lineHeight: '1' },
  scoreOf: { fontSize: '18px', color: 'rgba(255,255,255,0.35)', fontWeight: '400' },
  scoreRight: { flex: 1 },
  scoreBadge: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', background: 'rgba(212,168,71,0.15)', color: '#D4A847', padding: '4px 12px', display: 'inline-block', marginBottom: '8px' },
  scoreLevel: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: '#D4A847', marginBottom: '6px' },
  scoreTagline: { fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontWeight: '300', lineHeight: '1.6' },
  panelTitle: { fontFamily: 'Playfair Display, serif', fontSize: '17px', color: '#1B2A4A', marginBottom: '16px' },
  compareHead: { display: 'grid', gridTemplateColumns: '120px 1fr 60px 60px 60px', gap: '12px', padding: '8px 0', borderBottom: '2px solid #E8E6E2', marginBottom: '8px' },
  compareLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#8A8A8A' },
  compareRow: { display: 'grid', gridTemplateColumns: '120px 1fr 60px 60px 60px', gap: '12px', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #E8E6E2' },
  dimName: { fontSize: '12px', fontWeight: '600', color: '#1B2A4A' },
  compareScore: { textAlign: 'center', fontSize: '13px', color: '#3D3D3D' },
  ctaBanner: { background: '#C0392B', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  ctaTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#fff', marginBottom: '2px' },
  ctaSub: { fontSize: '12px', color: 'rgba(255,255,255,0.7)' },
  ctaBtn: { background: '#fff', color: '#C0392B', border: 'none', padding: '10px 22px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
};
